/**
 * MapLayerSwitcher — right-side vertical dock on desktop + expandable
 * bottom sheet on mobile. Fetches the manifest from `/api/map/layers`
 * so adding a new POI category is a purely backend change.
 *
 * P2b — Marker rendering:
 *   • Labels resolve through `useI18n().t()` (no hard-coded strings).
 *   • Icons come from the canonical `poiCategories.js` registry
 *     (lucide-react), NOT the emoji fallback returned by the backend.
 *   • The backend `id` remains the language-neutral canonical key.
 *
 * Emits `onToggle(layerId, active)` — the parent Map component owns the
 * actual bbox fetch + marker rendering.
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Layers, ChevronRight, ChevronLeft, X, Route } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import { getCategoryConfig } from "@/lib/poiCategories";

export default function MapLayerSwitcher({ active = [], onToggle, trafficEnabled = false, trafficActive = false, onToggleTraffic }) {
  const { t } = useI18n();
  const [layers, setLayers] = useState([]);
  const [open, setOpen]     = useState(true);   // desktop: expanded by default
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    let ok = true;
    api.get("/map/layers")
      .then(r => { if (ok) setLayers(r.data.layers || []); })
      .catch(() => { if (ok) setLayers([]); });
    return () => { ok = false; };
  }, []);
  if (!layers.length) return null;

  const chip = (l) => {
    const cfg = getCategoryConfig(l.id);
    const Icon = cfg.Icon;
    const on = active.includes(l.id);
    const label = cfg.tKey ? t(cfg.tKey, l.label || cfg.label) : (l.label || cfg.label);
    return (
      <button key={l.id} onClick={() => onToggle?.(l.id, !on)}
              className={`flex items-center gap-2 px-3 py-2 rounded-full border text-xs font-bold transition ${on
                ? "text-white border-transparent shadow-md"
                : "bg-white text-stone-700 border-stone-200 hover:border-stone-300"}`}
              style={on ? { backgroundColor: cfg.color, borderColor: cfg.color } : {}}
              data-testid={`map-layer-${l.id}`}
              aria-pressed={on}
              aria-label={label}
              title={label}>
        <Icon size={14} aria-hidden/>
        <span>{label}</span>
      </button>
    );
  };

  const trafficLabel = trafficActive
    ? t("map.traffic.on", "Live traffic · ON")
    : t("map.traffic.off", "Live traffic · OFF");

  return (
    <>
      {/* Desktop — right-side vertical dock */}
      <div className={`hidden md:flex fixed right-3 top-24 z-30 flex-col gap-2 transition-transform ${open ? "translate-x-0" : "translate-x-[calc(100%-40px)]"}`}
           data-testid="map-layer-switcher-desktop">
        <div className="bg-white/95 backdrop-blur rounded-2xl shadow-lg border border-stone-200 p-3 w-64 max-h-[70vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs uppercase tracking-widest font-black text-stone-500 flex items-center gap-1">
              <Layers size={14}/> {t("map.layers.title", "Map layers")}
            </div>
            <button onClick={() => setOpen(o => !o)} className="text-stone-400 hover:text-stone-800"
                    aria-label={open ? t("common.collapse", "Collapse") : t("common.expand", "Expand")}
                    data-testid="map-layer-collapse">
              {open ? <ChevronRight size={16}/> : <ChevronLeft size={16}/>}
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {layers.map(chip)}
          </div>
          {trafficEnabled && (
            <div className="mt-3 pt-3 border-t border-stone-100">
              <button onClick={() => onToggleTraffic?.(!trafficActive)}
                      className={`w-full text-xs font-bold rounded-full px-3 py-2 border inline-flex items-center justify-center gap-1.5 ${trafficActive
                        ? "bg-orange-500 text-white border-orange-600"
                        : "bg-white text-stone-700 border-stone-200 hover:border-stone-300"}`}
                      aria-pressed={trafficActive}
                      data-testid="map-traffic-toggle">
                <Route size={13}/> {trafficLabel}
              </button>
            </div>
          )}
          {!trafficEnabled && (
            <div className="mt-3 pt-3 border-t border-stone-100 text-[11px] text-stone-400" data-testid="map-traffic-disabled">
              {t("map.traffic.unavailable", "Live traffic unavailable — no provider configured")}
            </div>
          )}
        </div>
      </div>

      {/* Mobile — floating button opens a bottom sheet */}
      <button onClick={() => setMobileOpen(true)}
              aria-label={t("map.layers.open", "Open map layers")}
              className="md:hidden fixed right-3 bottom-24 z-40 bg-emerald-600 hover:bg-emerald-700 text-white rounded-full w-12 h-12 shadow-lg grid place-items-center"
              data-testid="map-layer-switcher-mobile-fab">
        <Layers size={20}/>
      </button>
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-[70] bg-black/50" onClick={() => setMobileOpen(false)}
             data-testid="map-layer-switcher-mobile-sheet">
          <div className="absolute inset-x-0 bottom-0 bg-white rounded-t-2xl p-4 max-h-[70vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <div className="font-black text-stone-900 flex items-center gap-1">
                <Layers size={16}/> {t("map.layers.title", "Map layers")}
              </div>
              <button onClick={() => setMobileOpen(false)}
                      aria-label={t("common.close", "Close")}
                      className="text-stone-400" data-testid="map-layer-mobile-close">
                <X size={20}/>
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {layers.map(chip)}
            </div>
            {trafficEnabled && (
              <button onClick={() => onToggleTraffic?.(!trafficActive)}
                      className={`mt-3 w-full text-xs font-bold rounded-full px-3 py-2 border inline-flex items-center justify-center gap-1.5 ${trafficActive
                        ? "bg-orange-500 text-white border-orange-600"
                        : "bg-white text-stone-700 border-stone-200"}`}
                      aria-pressed={trafficActive}
                      data-testid="map-traffic-toggle-mobile">
                <Route size={13}/> {trafficLabel}
              </button>
            )}
            {!trafficEnabled && (
              <div className="mt-3 text-[11px] text-stone-400 text-center">
                {t("map.traffic.unavailable", "Live traffic unavailable — no provider configured")}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
